/**
 * @file change-password.component.ts
 * @description
 * This component handles the "Change Password" functionality for the User Panel.
 * It allows users to request a password reset by accepting new password with old password verification.
 *
 * The component includes the following:
 * 1. A form with an input field for:
 *    - Old Password (Required)
 *    - New Password (Required)
 *    - Retype Password (Required)
 * 2. Form validation to ensure the field is properly filled.
 * 3. API integration to password reset request to the backend.
 * 4. Displays a success message if the reset email is sent successfully.
 * 5. Error handling and user feedback (invalid old password, network issues, etc.).
 * 6. Navigation option to return to the login page.
 *
 * @dependencies:
 * - Authentication Service for handling forgot password requests.
 * - Angular Reactive Forms (`@angular/forms`) for form handling and validation.
 * - Angular Router (`@angular/router`) for navigation.
 * - Angular Material Snackbar (`MatSnackBar`) for displaying success and error messages.
 *
 * @param {MatSnackBar} snackBar - Used to display notifications for success and error messages.
 */

import { Component } from '@angular/core';
import { encryptData, showNotification } from '../../../../consts/utils';
import { portalHttpRoutes, portalHttpVersion } from '../../../../consts/portal_http_routes';
import { UntypedFormGroup, UntypedFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, RouterModule } from '@angular/router';
import { ApiService } from '../../../../shared/services/api.services';
import { SeoService } from '../../../../shared/services/seo.service';

@Component({
  selector: 'app-change-password',
  imports: [ReactiveFormsModule, RouterModule],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss'
})

export class ChangePasswordComponent {
  form: UntypedFormGroup;
  token: any;
  oldShow: boolean = false;
  show: boolean = false;
  showConfirm: boolean = false;

  constructor(private _formBuilder: UntypedFormBuilder,
    private apiService: ApiService,
    private seo: SeoService,
    private snackBar: MatSnackBar,
    private router: Router,
  ) {
    /**
            * Sets up SEO meta tags using SeoService
            * This helps in optimizing the page for search engines and social media previews.
        */
    this.seo.setTags({
      title: 'User Portal Change Password screen', // Title
      titleSuffix: '- 10 Business Components', // Title Suffix
      description: '10 Business Components', // Description
      image: '', // Image
      keywords: '10 Business Components' // Keywords
    });
  }

  ngOnInit(): void {
    this.form = this._formBuilder.group({
      oldPassword: ['',
        [
          Validators.compose([
            Validators.required,
            Validators.maxLength(100)
          ])
        ],
      ],
      password: ['',
        [
          Validators.compose([
            Validators.required,
            Validators.maxLength(100)
          ])
        ],
      ],
      confirmPassword: ['',
        [
          Validators.compose([
            Validators.required,
            Validators.maxLength(100)
          ])
        ],
      ]
    });
  }

  /**
   * Reset Password API call - All Password is send encrypted form
   */

  resetPassword() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.form.value.password != this.form.value.confirmPassword) {
      showNotification(this.snackBar, 'Password and Retype password does not match', 'error');
      return;
    }

    const encryptedOldPassword = encryptData(this.form.value.oldPassword);
    const encryptedPassword = encryptData(this.form.value.password);
    const encryptedConfirmPassword = encryptData(this.form.value.confirmPassword);

    const formData = {
      oldPassword: encryptedOldPassword,
      password: encryptedPassword,
      passwordConfirm: encryptedConfirmPassword,
    };

    this.apiService.httpPostCall<any>(
      portalHttpVersion.V1 + portalHttpRoutes.PORTAL_CHANGE_PASSWORD,
      formData
    ).subscribe({
      next: (response: any) => {
        if (response.status) {
          showNotification(this.snackBar, response.message, 'success');
          this.router.navigate(['/portal/dashboard']);
        } else {
          showNotification(this.snackBar, response.message, 'error');
        }
      },
      error: (error: any) => {
        showNotification(this.snackBar, error.message, 'error');
      }
    });
  }
}
